import type { QueryClient } from '@tanstack/react-query';
import { createRouter } from '@tanstack/react-router';

import {
  RouteErrorBoundary,
  RouteNotFound,
} from '@/components/route-error-boundary';
import { routeTree } from '@/routes/tree';

/**
 * Build the application router.
 *
 * A factory taking the `QueryClient` rather than a module-level singleton: the
 * router's context has to hold the same cache instance the React tree provides,
 * and passing it in is the only way to guarantee that instead of hoping two
 * imports resolve to the same module.
 */
export const createAppRouter = (queryClient: QueryClient) =>
  createRouter({
    routeTree,
    context: { queryClient },
    /*
     * Preload on hover/focus. Every navigation in Atlas is a deliberate click on
     * a card, so by the time the click lands the data is usually already there.
     */
    defaultPreload: 'intent',
    defaultPreloadStaleTime: 0,
    defaultErrorComponent: RouteErrorBoundary,
    defaultNotFoundComponent: RouteNotFound,
    scrollRestoration: true,
  });

/** The concrete router type, so `Link`, `useParams` and friends stay typed. */
export type AppRouter = ReturnType<typeof createAppRouter>;

declare module '@tanstack/react-router' {
  interface Register {
    router: AppRouter;
  }
}
